<template>
  <!-- Read-only viewers (customers, the card view) get a single static chip.
       The interactive P0-P3 row reads as four buttons and hover-highlights
       whichever level is under the cursor, so a customer tries to click one and
       nothing happens. Only render a control where there is one. -->
  <span
    v-if="readonly"
    class="inline-block px-1.5 py-0.5 text-[11px] font-medium rounded"
    :class="badgeClass"
    :title="badgeTitle"
  >{{ badgeLabel }}</span>
  <div v-else class="inline-flex items-center gap-0.5">
    <button
      v-for="level in levels"
      :key="level.value"
      type="button"
      class="px-1.5 py-0.5 text-[11px] font-medium rounded transition-colors cursor-pointer"
      :class="
        modelValue === level.value
          ? level.activeClass
          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      "
      :title="titleFor(level)"
      :aria-label="titleFor(level)"
      :aria-pressed="modelValue === level.value"
      @click="toggle(level.value)"
    >
      {{ level.label }}
    </button>
    <!-- Clearing by clicking the selected level again is invisible unless you
         happen to try it. Where there's room for it (the drawer), offer a real
         control; dense table cells opt out via the default. -->
    <button
      v-if="clearable && modelValue != null"
      type="button"
      class="ml-0.5 px-1 py-0.5 text-[10px] rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
      title="Clear priority"
      aria-label="Clear priority"
      @click="$emit('update:modelValue', null)"
    >
      <FontAwesomeIcon :icon="['fas', 'xmark']" />
    </button>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  PRIORITY_LEVELS,
  PRIORITY_LABELS,
  priorityShortLabel,
  priorityClass,
  priorityLabel,
} from './support_constants';

export default {
  name: 'Support_PriorityRating',
  components: { FontAwesomeIcon },
  props: {
    modelValue: { type: Number, default: null },
    readonly: { type: Boolean, default: false },
    // Show an explicit clear (x) button next to the levels.
    clearable: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return { levels: PRIORITY_LEVELS };
  },
  computed: {
    badgeLabel() {
      return priorityShortLabel(this.modelValue);
    },
    badgeClass() {
      return priorityClass(this.modelValue);
    },
    badgeTitle() {
      return this.modelValue == null
        ? 'No priority set'
        : priorityLabel(this.modelValue).replace(/^(P\d)\s+/, '$1 · ');
    },
  },
  methods: {
    labelFor(level) {
      // e.g. "P0 · Critical" — spells out the jargon on hover.
      const full = PRIORITY_LABELS[level.code] || level.label;
      return full.replace(/^(P\d)\s+/, '$1 · ');
    },
    titleFor(level) {
      // Say so on the selected level, so the toggle-to-clear gesture is
      // discoverable even where the explicit clear button isn't shown.
      return this.modelValue === level.value
        ? `${this.labelFor(level)} — click to clear`
        : this.labelFor(level);
    },
    toggle(value) {
      this.$emit('update:modelValue', this.modelValue === value ? null : value);
    },
  },
};
</script>
